import { Tune as Settings2Icon } from '@mui/icons-material';
import { Box, Button, Checkbox, Divider, Popover, Stack, Typography } from '@mui/material';
import { useState, MouseEvent } from 'react';

export type ValueMetric = 'quantity' | 'weight' | 'volume';
export type InfoMetric =
  | 'origin_dest'
  | 'section_pref'
  | 'section_city'
  | 'address'
  | 'shipper'
  | 'time_window'
  | 'conditions';
export type DisplayMetric = ValueMetric | InfoMetric;

const VALUE_METRICS: { key: ValueMetric; label: string }[] = [
  { key: 'quantity', label: '数量' },
  { key: 'weight', label: '重量' },
  { key: 'volume', label: '体積' },
];

const INFO_METRICS: { key: InfoMetric; label: string }[] = [
  { key: 'origin_dest', label: '積降地' },
  { key: 'section_pref', label: '区間(都道府県)' },
  { key: 'section_city', label: '区間(市区町村)' },
  { key: 'address', label: '住所' },
  { key: 'shipper', label: '荷主名' },
  { key: 'time_window', label: '指定時間' },
  { key: 'conditions', label: '条件' },
];

interface Props {
  value: DisplayMetric[];
  onChange: (metrics: DisplayMetric[]) => void;
}

const DisplayMetricToggle = ({ value, onChange }: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const valueSet = new Set(value);
  const activeCount = value.length;

  const toggle = (metric: DisplayMetric) => {
    if (valueSet.has(metric)) {
      const next = value.filter((m) => m !== metric);
      if (next.length === 0) return;
      onChange(next);
    } else {
      onChange([...value, metric]);
    }
  };

  const handleOpen = (e: MouseEvent<HTMLButtonElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <Button
        size="small"
        onClick={handleOpen}
        startIcon={<Settings2Icon sx={{ fontSize: 14 }} />}
        sx={{
          height: 28,
          minWidth: 0,
          px: 1,
          fontSize: 10,
          fontWeight: 600,
          textTransform: 'none',
          color: 'text.secondary',
          gap: 0.5,
          '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
        }}
      >
        表示項目
        {activeCount > 0 && (
          <Box
            component="span"
            sx={{
              ml: 0.25,
              px: 0.75,
              py: 0.25,
              borderRadius: 999,
              bgcolor: 'hsl(var(--primary) / 0.15)',
              color: 'primary.main',
              fontSize: 9,
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            {activeCount}
          </Box>
        )}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 208, p: 0 } } }}
      >
        <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
          <Typography
            variant="caption"
            sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}
          >
            数値指標
          </Typography>
        </Box>
        <Stack sx={{ px: 1, pb: 1 }} spacing={0.25}>
          {VALUE_METRICS.map(({ key, label }) => (
            <Stack
              key={key}
              direction="row"
              alignItems="center"
              spacing={1}
              component="label"
              sx={{ px: 1, py: 0.5, borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
            >
              <Checkbox
                size="small"
                checked={valueSet.has(key)}
                onChange={() => toggle(key)}
                sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: 14 } }}
              />
              <Typography variant="caption" sx={{ fontSize: 12, fontWeight: 500, color: 'text.primary' }}>
                {label}
              </Typography>
            </Stack>
          ))}
        </Stack>

        <Divider />

        <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
          <Typography
            variant="caption"
            sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}
          >
            表示情報
          </Typography>
        </Box>
        <Stack sx={{ px: 1, pb: 1.5 }} spacing={0.25}>
          {INFO_METRICS.map(({ key, label }) => (
            <Stack
              key={key}
              direction="row"
              alignItems="center"
              spacing={1}
              component="label"
              sx={{ px: 1, py: 0.5, borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
            >
              <Checkbox
                size="small"
                checked={valueSet.has(key)}
                onChange={() => toggle(key)}
                sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: 14 } }}
              />
              <Typography variant="caption" sx={{ fontSize: 12, fontWeight: 500, color: 'text.primary' }}>
                {label}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Popover>
    </>
  );
};

export default DisplayMetricToggle;
